import { useEffect, useRef } from 'react'
import { useI18n } from '../i18n'

/**
 * Cloudflare Turnstile human check for the sign-up form. It is off until a site key is built in
 * (VITE_TURNSTILE_SITE_KEY), and the API only asks for a token once its TURNSTILE_SECRET is set, so the two
 * are switched on separately. With no key this renders nothing and loads nothing.
 */
interface TurnstileApi {
  render(el: HTMLElement, options: Record<string, unknown>): string
  reset(id?: string): void
  remove?(id?: string): void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/** The site key, or '' when none is built in. */
export function turnstileSiteKey(): string {
  return (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ?? ''
}

let scriptPromise: Promise<TurnstileApi> | undefined

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  scriptPromise ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SCRIPT
    script.async = true
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')))
    script.onerror = () => {
      scriptPromise = undefined
      reject(new Error('turnstile'))
    }
    document.head.append(script)
  })
  return scriptPromise
}

interface Props {
  /** The current token, or null while unsolved, expired or failed. */
  onToken: (token: string | null) => void
  /** Change this number to ask for a new check; a token works once. */
  round?: number
}

export function TurnstileCheck({ onToken, round = 0 }: Props) {
  const siteKey = turnstileSiteKey()
  const { locale } = useI18n()
  const box = useRef<HTMLDivElement>(null)
  const widget = useRef<string | undefined>(undefined)
  const notify = useRef(onToken)
  notify.current = onToken

  useEffect(() => {
    const el = box.current
    if (!siteKey || !el) return
    let cancelled = false
    loadTurnstile()
      .then((api) => {
        if (cancelled) return
        widget.current = api.render(el, {
          sitekey: siteKey,
          theme: 'auto',
          // Unobtrusive: the widget fills the form width and stays hidden unless Cloudflare needs the visitor to act.
          size: 'flexible',
          appearance: 'interaction-only',
          language: locale === 'sq' ? 'sq' : 'en',
          callback: (token: string) => notify.current(token),
          'expired-callback': () => notify.current(null),
          'error-callback': () => notify.current(null),
        })
      })
      // The form stays usable; the API answers 403 and the form shows the security-check message.
      .catch(() => notify.current(null))
    return () => {
      cancelled = true
      if (widget.current) window.turnstile?.remove?.(widget.current)
      widget.current = undefined
    }
  }, [siteKey, locale])

  useEffect(() => {
    if (round > 0 && widget.current) window.turnstile?.reset(widget.current)
  }, [round])

  if (!siteKey) return null
  return <div ref={box} data-turnstile="" />
}
